import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Isvg from 'react-inlinesvg';

import { PFE_ICONS } from 'constants/icons';
import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import Message from 'components/Message';
import { actionsMessages as messages } from './ScheduleDetail.messages';
import './ScheduleDetailActions.scss';

const EDIT_DROPDOWN_ITEMS = [
  {
    key: 'EDIT_INSTRUCTOR',
    shouldRender: ({ isInExtendedActionsWindow, isInThePast, isMsInventoryVenue, schedule }) =>
      !isInExtendedActionsWindow && !isInThePast && isMsInventoryVenue && !schedule.isDisabled,
    render: () => {
      return <Message className="schedule-detail-actions__item--edit-instructor" message={messages.editInstructor} />;
    },
    action: ({ redirectToManageSchedule }) => redirectToManageSchedule(),
  },
  {
    key: 'MANAGE_CLASS',
    shouldRender: () => true,
    render: function ScheduleDetailActionsManageClassRender() {
      return <Message message={messages.manageClass} />;
    },
    action: ({ redirectToManageClass }) => redirectToManageClass(),
  },
  {
    key: 'EMAIL_ALL',
    shouldRender: ({ hasReservations, schedule }) => hasReservations && !schedule.isDisabled,
    render: function ScheduleDetailActionsEmailAllRender() {
      return <Message message={messages.emailAll} />;
    },
    action: ({ openModal, emailScheduleAttendees, schedule, reservationsCount, venueName, venueSubtitle }) =>
      openModal('EmailModal', {
        onSubmit: emailScheduleAttendees,
        title: <Message message={messages.toTitle} />,
        to: <Message message={messages.toContent} values={{ reservations: reservationsCount }} />,
        subject: (
          <Message
            message={messages.subjectContent}
            values={{
              startDateTime: schedule.startDateTime,
              venueNameSubtitle: `${venueName}${venueSubtitle ? ` (${venueSubtitle})` : ''}`,
            }}
          />
        ),
        initialValues: {
          schedule,
          newAttendeesOnly: false,
          message: '',
        },
      }),
  },
  {
    key: 'EMAIL_NEW',
    shouldRender: ({ hasNewReservations, hasReservations, isInExtendedActionsWindow, schedule }) =>
      hasNewReservations && hasReservations && !isInExtendedActionsWindow && !schedule.isDisabled,
    render: function ScheduleDetailActionsEmailNewRender() {
      return <Message message={messages.emailNew} />;
    },
    action: ({ openModal, emailScheduleAttendees, schedule, newReservationsCount, venueName, venueSubtitle }) =>
      openModal('EmailModal', {
        onSubmit: emailScheduleAttendees,
        title: <Message message={messages.newReservationsTitle} />,
        to: <Message message={messages.toContent} values={{ reservations: newReservationsCount }} />,
        subject: (
          <Message
            message={messages.subjectContent}
            values={{
              startDateTime: schedule.startDateTime,
              venueNameSubtitle: `${venueName}${venueSubtitle ? ` (${venueSubtitle})` : ''}`,
            }}
          />
        ),
        initialValues: {
          schedule,
          newAttendeesOnly: true,
          message: '',
        },
      }),
  },
  {
    key: 'STOP_RESERVATIONS',
    shouldRender: ({ isReservationsStoppable, reservationsStopped, isInExtendedActionsWindow, schedule }) =>
      isReservationsStoppable && !reservationsStopped && !isInExtendedActionsWindow && !schedule.isDisabled,
    render: function ScheduleDetailActionsStopReservationsRender() {
      return (
        <Message className="schedule-detail-actions__item--stop-reservations" message={messages.stopReservations} />
      );
    },
    action: ({ stopAllowingReservations }) => stopAllowingReservations(),
  },
  {
    key: 'ALLOW_RESERVATIONS',
    shouldRender: ({ isReservationsStoppable, reservationsStopped, isInExtendedActionsWindow, schedule }) =>
      isReservationsStoppable && reservationsStopped && !isInExtendedActionsWindow && !schedule.isDisabled,
    render: function ScheduleDetailActionsAllowReservationsRender() {
      return (
        <Message className="schedule-detail-actions__item--allow-reservations" message={messages.allowReservations} />
      );
    },
    action: ({ startAllowingReservations }) => startAllowingReservations(),
  },
  {
    key: 'CANCEL_SINGLE',
    shouldRender: ({ cannotCancelClass, schedule }) => {
      const { isCancellable, isDisabled } = schedule;
      return !cannotCancelClass && isCancellable && !isDisabled;
    },
    render: function ScheduleDetailActionsCancelSingleRender() {
      return <Message className="schedule-detail-actions__item--cancel-single" message={messages.cancelSingle} />;
    },
    action: ({ openCancelScheduleModal, schedule, cancelSchedule, isMsInventoryVenue }) =>
      openCancelScheduleModal({
        isRecurring: false,
        activeSchedule: schedule,
        cancelSchedule,
        isMsInventoryVenue,
      }),
  },
  {
    key: 'CANCEL_RECURRING',
    shouldRender: ({ isMsInventoryVenue, cannotCancelClass, schedule, shouldShowCancelRecurring }) => {
      const { isCancellable, isDisabled } = schedule;
      return !cannotCancelClass && isCancellable && !isDisabled && !isMsInventoryVenue && shouldShowCancelRecurring;
    },
    render: function ScheduleDetailActionsCancelRecurringRender() {
      return <Message className="schedule-detail-actions__item--cancel-recurring" message={messages.cancelRecurring} />;
    },
    action: ({ openModal, schedule, cancelSchedule }) =>
      openModal('CancelScheduleModal', { isRecurring: true, activeSchedule: schedule, cancelSchedule }),
  },
];

const ScheduleDetailActions = props => {
  const { className, isDesktop } = props;

  return (
    <Dropdown className={classNames('schedule-detail-actions', className)} attachment="right">
      {({ toggleMenu }) => (
        <>
          <button type="button" className="schedule-detail-actions__toggle" onClick={toggleMenu}>
            {isDesktop ? <Message message={messages.options} /> : <Isvg src={PFE_ICONS.threeDots} />}
          </button>
          <DropdownMenu>
            {EDIT_DROPDOWN_ITEMS.filter(item => item.shouldRender(props)).map(item => (
              <DropdownItem key={item.key} onClick={() => item.action(props)}>
                {item.render()}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </>
      )}
    </Dropdown>
  );
};

ScheduleDetailActions.propTypes = {
  className: PropTypes.string,
  isDesktop: PropTypes.bool,
  // the linter has trouble seeing that the following are used in
  // the EDIT_DROPDOWN_ITEM `shouldRender` and `action` functions
  // emailScheduleAttendees: PropTypes.func,
  // hasNewReservations: PropTypes.bool,
  // reservationsStopped: PropTypes.bool,
  // isInThePast: PropTypes.bool,
  // isReservationsStoppable: PropTypes.bool,
  // openModal: PropTypes.func.isRequired,
  // shouldShowCancelRecurring: PropTypes.bool,
  // startAllowingReservations: PropTypes.func,
  // stopAllowingReservations: PropTypes.func,
  // isMsInventoryVenue: PropTypes.bool,
  // cannotCancelClass: PropTypes.bool,
};

export default ScheduleDetailActions;
